/**
 * The one place a command refused by fire mode is explained, for every
 * command in the product at once.
 *
 * WHY IT IS GLOBAL AND NOT PER-BUTTON. `fire_mode_active` can come back
 * from any of the frozen capabilities — dispatch, resume, rewind,
 * direct action, recommission — issued from the task drawer, the robot
 * drawer, the dispatch form, the calendar, a favourite. Each of those
 * renders `error.message` in its own way, and `ApiClientError`'s
 * message for this failure is the literal string "api error" (see
 * `useFireMode.ts::apiErrorCode` for why). Teaching every one of them
 * the difference is a dozen edits that the next command path will not
 * inherit; watching the mutation cache catches all of them, including
 * the ones written after this.
 *
 * It is not a toast: `ToastStack` is driven by the alert notification
 * provider and is where site alerts land. This is a direct consequence
 * of something the operator just pressed, so it stays put until
 * dismissed rather than timing out while they read it.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertBanner } from '../design-system/components/AlertBanner';
import {
  FIRE_MODE_HOLD_COPY,
  FIRE_MODE_HOLD_TITLE,
  isFireModeHold,
  useFirePanelButtonLabel,
} from '../api/hooks/useFireMode';
import { useFireAlarm } from '../state/useFireAlarm';
import './FireModeHoldNotice.css';

export function FireModeHoldNotice() {
  const qc = useQueryClient();
  const { openFireModePanel, isFireAlarmActive } = useFireAlarm();
  const [heldAt, setHeldAt] = useState<number | null>(null);
  // "Fire mode status" for a reader who can only look (review BC-F12).
  const firePanelLabel = useFirePanelButtonLabel();

  // THE NOTICE ENDS WITH THE EVENT. It used to stay up after a supervisor
  // resolved the alarm, still saying "This site is frozen" over a site
  // that was running again (fire-flow review 2026-10-22). Cleared on the
  // active -> clear TRANSITION only: a refusal can arrive before this
  // browser has learned the alarm is on at all, and that notice is true.
  const wasActive = useRef(isFireAlarmActive);
  useEffect(() => {
    if (wasActive.current && !isFireAlarmActive) setHeldAt(null);
    wasActive.current = isFireAlarmActive;
  }, [isFireAlarmActive]);

  useEffect(() => {
    return qc.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      if (event.mutation.state.status !== 'error') return;
      if (!isFireModeHold(event.mutation.state.error)) return;
      // Re-stamped rather than toggled, so a second refused command
      // while the first notice is still up reads as a fresh refusal
      // instead of appearing to do nothing.
      setHeldAt(Date.now());
    });
  }, [qc]);

  // PUBLISHES ITS OWN HEIGHT so the toast stack can sit above it.
  // Both are `position: fixed; bottom: 24px`; this one is centred at
  // up to 560px wide and the toasts are right-aligned at up to 360px,
  // so they overlapped on any viewport under about 1300px -- and this
  // notice explains why a command the operator just pressed was
  // refused, which is not a thing to have a toast land on top of.
  // Zero when absent, so the toasts return to the bottom.
  const holdRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = holdRef.current;
    const clear = () =>
      document.documentElement.style.setProperty('--nx-fire-hold-h', '0px');
    if (!el) {
      clear();
      return undefined;
    }
    const publish = () => {
      document.documentElement.style.setProperty(
        '--nx-fire-hold-h',
        `${Math.round(el.getBoundingClientRect().height) + 12}px`,
      );
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return clear;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      clear();
    };
  }, [heldAt]);

  if (heldAt === null) return null;

  return (
    <div className="nx-fire-hold" role="alert" data-testid="fire-mode-hold-notice" ref={holdRef}>
      <AlertBanner
        variant="warning"
        title={FIRE_MODE_HOLD_TITLE}
        actions={
          <>
            <button
              type="button"
              className="nx-fire-hold__btn"
              onClick={openFireModePanel}
              data-testid="fire-hold-open-panel"
            >
              {firePanelLabel}
            </button>
            <button
              type="button"
              className="nx-fire-hold__btn nx-fire-hold__btn--quiet"
              onClick={() => setHeldAt(null)}
              aria-label="Dismiss"
              data-testid="fire-hold-dismiss"
            >
              Dismiss
            </button>
          </>
        }
      >
        {FIRE_MODE_HOLD_COPY}
      </AlertBanner>
    </div>
  );
}
